// "Add VTS to your Home Screen" — one-tap app-like access to the bot.
// Auto-opens ONCE on mobile; always available from the header. On Android,
// uses the native install prompt when the browser offers it.
import React, { useEffect, useState } from "react";
import { Smartphone, X, Share, SquarePlus, MoreVertical, Download } from "lucide-react";

const SEEN_KEY = "vts_a2hs_seen_v1";
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

export const shouldAutoShowA2HS = () => {
  try {
    if (localStorage.getItem(SEEN_KEY)) return false;
    if (isStandalone()) return false;
    return /Android|iPad|iPhone|iPod/.test(navigator.userAgent);
  } catch {
    return false;
  }
};

export const markA2HSSeen = () => {
  try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* private mode */ }
};

const AddToHomeGuide = ({ onClose }) => {
  const [ios] = useState(isIOS());
  const [canNativeInstall, setCanNativeInstall] = useState(!!deferredPrompt);

  useEffect(() => { markA2HSSeen(); }, []);

  const nativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanNativeInstall(false);
    onClose();
  };

  const Step = ({ n, children }) => (
    <div className="flex gap-2.5">
      <span className="shrink-0 h-5 w-5 rounded-full bg-vts-blue/20 border border-vts-blue/50 text-vts-blue text-[11px] font-bold flex items-center justify-center">{n}</span>
      <p>{children}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80]">
      <div data-testid="a2hs-backdrop" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="a2hs-modal" className="absolute inset-x-3 top-1/2 -translate-y-1/2 max-w-md mx-auto rounded-2xl border border-vts-line bg-vts-card shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-vts-line">
          <Smartphone className="h-4 w-4 text-vts-blue" />
          <span className="text-sm font-bold text-white">Add VTS to your Home Screen</span>
          <button data-testid="a2hs-close" onClick={onClose} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
        </div>
        <div className="p-4 space-y-3 text-[12px] text-vts-silver/85">
          <p className="text-vts-silver/70">Open the bot in one tap — like a real app, full screen, no browser bar.</p>
          {canNativeInstall && (
            <button data-testid="a2hs-native-install" onClick={nativeInstall}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-vts-blue text-white font-bold text-[12px] py-2.5 hover:bg-vts-blueHover transition">
              <Download className="h-3.5 w-3.5" /> Install now (one tap)
            </button>
          )}
          {ios ? (
            <div className="space-y-3" data-testid="a2hs-ios-steps">
              <p className="text-[10px] uppercase tracking-wider text-vts-silver/45 font-mono">iPhone / iPad (Safari)</p>
              <Step n={1}>Tap the <span className="font-semibold text-white">Share</span> button <Share className="inline h-3.5 w-3.5 text-vts-blue" /> at the bottom of Safari.</Step>
              <Step n={2}>Scroll down and tap <span className="font-semibold text-white">Add to Home Screen</span> <SquarePlus className="inline h-3.5 w-3.5 text-vts-blue" />.</Step>
              <Step n={3}>Tap <span className="font-semibold text-white">Add</span> — the VTS icon appears on your Home Screen.</Step>
            </div>
          ) : (
            <div className="space-y-3" data-testid="a2hs-android-steps">
              <p className="text-[10px] uppercase tracking-wider text-vts-silver/45 font-mono">Android (Chrome)</p>
              <Step n={1}>Tap the <span className="font-semibold text-white">menu</span> <MoreVertical className="inline h-3.5 w-3.5 text-vts-blue" /> (three dots, top right).</Step>
              <Step n={2}>Tap <span className="font-semibold text-white">Add to Home screen</span> (or <span className="font-semibold text-white">Install app</span>).</Step>
              <Step n={3}>Confirm — the VTS icon appears on your Home Screen.</Step>
            </div>
          )}
          <p className="text-[10px] text-vts-silver/45">Tip: opening from the icon takes you straight to your trading desk — check the bot, sims and P&L anytime.</p>
        </div>
      </div>
    </div>
  );
};

export default AddToHomeGuide;
